
import { Button, FormControlLabel, Switch, TextField } from '@mui/material'
import { LocalizationProvider, TimeField } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import React, { useEffect, useMemo, useState } from 'react'

import './settings.css';
import ConfirmModal from '../../components/confirmModal';
import BootstrapTooltip from '../../components/tooltip';
import ViewSchedulesModal from '../../components/viewSchedulesModal';

const LAYOUTS = {
  SETTINGS: 0,
  NEW: 1,
  EDIT: 2
};

const DEFAULT_COMPENSATION_TIME = {
  time: '00:00',
  type: 'late'
};

const normalizeCompensationTime = (value) => {
  if (typeof value === 'string') {
    return {
      ...DEFAULT_COMPENSATION_TIME,
      time: value
    };
  }

  return {
    ...DEFAULT_COMPENSATION_TIME,
    ...(value || {})
  };
};

const compensationTimeToDayjs = (time) => {
  const [minutes = '0', seconds = '0'] = `${time || '00:00'}`.split(':');

  return dayjs()
    .hour(0)
    .minute(Math.min(59, Number(minutes) || 0))
    .second(Math.min(59, Number(seconds) || 0))
    .millisecond(0);
};

const dayjsToCompensationTime = (value) => {
  if (!value || !value.isValid()) return '00:00';

  return `${value.minute().toString().padStart(2, '0')}:${value.second().toString().padStart(2, '0')}`;
};

const Settings = ({ toleranceTime, userUpdate, periodicGroups, compensationMode, compensationTime, schedulesGroups, userSchedules, devices, scheduleRegister, scheduleGroupRegister, scheduleGroupDelete, scheduleGroupUpdate, updateSchedule }) => {

  const [tolTime, setTolTime] = useState(toleranceTime);
  const [localCompensationMode, setLocalCompensationMode] = useState(compensationMode);
  const [localCompensationTime, setLocalCompensationTime] = useState(normalizeCompensationTime(compensationTime));

  const canSave = useMemo(() => tolTime !== toleranceTime, [tolTime, toleranceTime]);
  const canSaveCompensation = useMemo(() => {
    const initialCompensationTime = normalizeCompensationTime(compensationTime);

    return (
      localCompensationMode !== compensationMode ||
      localCompensationTime.time !== initialCompensationTime.time ||
      localCompensationTime.type !== initialCompensationTime.type
    );
  }, [localCompensationMode, compensationMode, localCompensationTime, compensationTime]);

  const [showedLayout, setShowedLayout] = useState(LAYOUTS.SETTINGS);

  const [isUpdatingPeriodic, setIsUpdatingPeriodic] = useState(false);
  const [localPeriodic, setLocalPeriodic] = useState(periodicGroups);
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);

  const [groupName, setGroupName] = useState('');
  const [groupTime, setGroupTime] = useState('');
  const [groupEnabled, setGroupEnabled] = useState(false);

  const [availableSchedules, setAvailableSchedules] = useState([]);
  const [selectedSchedules, setSelectedSchedules] = useState([]);

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [pendingVehicleChange, setPendingVehicleChange] = useState(null);

  const [openViewModal, setOpenViewModal] = useState(false);
  const [prevSchedule, setPrevSchedule] = useState([]);

  const [groupToDelete, setGroupToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const [editingGroup, setEditingGroup] = useState(null);

  const [viewGroup, setViewGroup] = useState(null);
  const [isViewGroupOpen, setIsViewGroupOpen] = useState(false);

  const [groupToApply, setGroupToApply] = useState(null);
  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false);

  const nameExists = schedulesGroups.some(g => {
    if (editingGroup && g._id === editingGroup._id) {
      return false;
    }
    return (g.name.toLowerCase().trim() === groupName.toLowerCase().trim());

  });

  useEffect(() => {
    setLocalPeriodic(periodicGroups);
  }, [periodicGroups]);

  useEffect(() => {
    setLocalCompensationMode(compensationMode);
  }, [compensationMode]);

  useEffect(() => {
    setLocalCompensationTime(normalizeCompensationTime(compensationTime));
  }, [compensationTime]);

  const canSaveGroup =
    groupName.trim() !== '' &&
    !nameExists &&
    selectedSchedules.length > 0;

  const handleSaveCompensation = () => {
    userUpdate({
      compensationMode: localCompensationMode,
      compensationTime: localCompensationTime
    });
  };

  const handlePeriodicChange = async (event) => {
    const value = event.target.checked;
    setLocalPeriodic(value);
    setIsUpdatingPeriodic(true);

    try {
      await userUpdate({ periodicGroups: value }, false, false);
    } catch (error) {
      setLocalPeriodic(periodicGroups); // revertir si falla
    } finally {
      setIsUpdatingPeriodic(false);
    }
  };

  const applyVehicleChange = (scheduleId, parsedVehicleId) => {

    setSelectedSchedules(prev =>
      prev.map(s => {

        if (s.vehicleId === parsedVehicleId && s._id !== scheduleId) {
          return { ...s, vehicleId: null }; // desasignar del anterior
        }

        if (s._id === scheduleId) {
          return { ...s, vehicleId: parsedVehicleId }; // asignar al nuevo
        }

        return s;
      })
    );
  };

  const handleVehicleChange = (scheduleId, newVehicleId) => {

    const parsedVehicleId = newVehicleId ? parseInt(newVehicleId) : null;

    if (!parsedVehicleId) {
      setSelectedSchedules(prev =>
        prev.map(s =>
          s._id === scheduleId
            ? { ...s, vehicleId: null }
            : s
        )
      );
      return;
    }

    const isAlreadyAssigned = selectedSchedules.some(
      s => s.vehicleId === parsedVehicleId && s._id !== scheduleId
    );

    if (isAlreadyAssigned) {
      setPendingVehicleChange({ scheduleId, parsedVehicleId });
      setIsConfirmModalOpen(true);
    } else {
      applyVehicleChange(scheduleId, parsedVehicleId);
    }
  };

  const resetGroupForm = () => {
    setGroupName('');
    setGroupTime('');
    setGroupEnabled(false);

    setAvailableSchedules(structuredClone(userSchedules));
    setSelectedSchedules([]);

    setEditingGroup(null);
  };

  const handleDeleteGroup = (group) => {
    setGroupToDelete(group);
    setIsDeleteModalOpen(true);
  };

  const openEditGroup = (group) => {

    setEditingGroup(group);

    setGroupName(group.name || '');
    setGroupTime(group.startTime || '');
    setGroupEnabled(group.enabled || false);

    const flatSchedules = group.schedules?.flat() || [];

    const selected = flatSchedules.map(s => {
      const schedule = userSchedules.find(us => us._id === s.scheduleId);

      if (!schedule) return null;

      return { ...schedule, vehicleId: s.vehicleId || null };
    }).filter(Boolean);

    const selectedIds = selected.map(s => s._id);

    const available = userSchedules.filter(s => !selectedIds.includes(s._id));

    setSelectedSchedules(selected);
    setAvailableSchedules(available);

    setShowedLayout(LAYOUTS.EDIT);
  };

  const handleViewGroup = (group) => {
    if (!group) return;

    const flat = group.schedules?.flat() || [];

    const schedules = flat.map(s => {
      const schedule = userSchedules.find(us => us._id === s.scheduleId);

      if (!schedule) return null;

      return { ...schedule, vehicleId: s.vehicleId || null };

    }).filter(Boolean);

    setViewGroup({ ...group, schedulesParsed: schedules });

    setIsViewGroupOpen(true);
  };

  const handleApplyGroup = (group) => {
    setGroupToApply(group);
    setIsApplyModalOpen(true);
  };

  const confirmApplyGroup = () => {
    if (!groupToApply) return;
    const flat = groupToApply.schedules?.flat() || [];
    if (flat.length === 0) return;

    // Crear copia de horarios actuales
    const updatedAllSchedules = userSchedules.map(schedule => {
      const match = flat.find(s => s.scheduleId === schedule._id);

      if (!match) return schedule;

      return { ...schedule, vehicleId: match.vehicleId || null };
    });

    // Solo enviar los que realmente cambian
    const schedulesToUpdate = updatedAllSchedules.filter(schedule => {
      const original = userSchedules.find(s => s._id === schedule._id);

      return original?.vehicleId !== schedule.vehicleId;
    });

    if (schedulesToUpdate.length > 0) {
      updateSchedule(updatedAllSchedules, schedulesToUpdate);
    }

    setIsApplyModalOpen(false);
    setGroupToApply(null);
  };

  const renderLayout = () => {

    switch (showedLayout) {

      case LAYOUTS.NEW:
        return renderNewGroup();

      case LAYOUTS.EDIT:
        return renderEditGroup();

      default:
        return renderSettings();
    }
  };

  const confirmDeleteGroup = () => {

    if (!groupToDelete) return;

    scheduleGroupDelete([groupToDelete.name]);

    setIsDeleteModalOpen(false);
    setGroupToDelete(null);
  };

  const saveGroup = () => {

    if (!groupName.trim()) return;

    const formattedSchedules =
      selectedSchedules.map(s => [{
        scheduleId: s._id,
        vehicleId: s.vehicleId
      }]);

    const payload = {
      name: groupName.trim(),
      startTime: groupTime || null,
      enabled: groupEnabled,
      schedules: formattedSchedules
    };

    if (editingGroup) {
      scheduleGroupUpdate([
        {
          ...payload,
          _id: editingGroup._id,
          userId: editingGroup.userId
        }
      ]);

    } else {
      scheduleGroupRegister([payload]);
    }

    resetGroupForm();
    setEditingGroup(null);
    setShowedLayout(LAYOUTS.SETTINGS);

  };

  const renderSettings = () => (
    <>
      <div>
        <h2>Tiempo de tolerancia</h2>
        <div className='settings-actions'>
          <TextField
            fullWidth
            type="number"
            variant="outlined"
            value={tolTime}
            onChange={(e) => {
              const n = Number(e.target.value);
              if (Number.isNaN(n)) setTolTime(30);
              else setTolTime(Math.min(59, Math.max(1, n)));
            }}
            onBlur={() => {
              setTolTime((prev) => {
                if (prev === '' || prev == null) return 1;
                return Math.min(59, Math.max(1, Number(prev)));
              });
            }}
            slotProps={{
              input: {
                inputProps: { min: 1, max: 59, step: 1 },
                onClick: (event) => event.target.select(),
              },
            }}
            sx={{
              maxWidth: '70px',
              '& .MuiInputLabel-root': {
                color: 'var(--color-dark)', // Cambia el color de la label
              },
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'var(--color-dark-variant)', // Color del borde por defecto
                },
                '&:not(.Mui-focused):hover fieldset': {
                  borderColor: 'var(--color-dark)', // Color del borde al pasar el mouse
                },
              },
              '& .MuiInputBase-input': {
                fontSize: '0.8rem', // Tamaño del texto dentro del campo de entrada
                padding: '0.5rem 12px', // Ajuste de padding para alinear correctamente el texto
                color: 'var(--color-dark)', // Color del texto
              }
            }}
          />
          <span style={{ fontSize: '14px', marginRight: '2rem' }}>segundos</span>
          <Button
            variant="contained"
            color='success'
            size='small'
            onClick={() => { userUpdate({ toleranceTime: tolTime }) }}
            disabled={!canSave}
          >
            Guardar
          </Button>
        </div>
      </div>
      <div>
        <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          Modo compensación de horario
          <BootstrapTooltip title="Modalidad que aplicará un ajuste de tiempo a los horarios registrados, adelantando o retrasando los tiempos de llegada según el tiempo que se defina a continuación." placement="right">
            <span className="material-symbols-rounded" style={{ fontSize: '18px', color: 'var(--color-dark-variant)', cursor: 'pointer', alignSelf: 'center' }}>
              help
            </span>
          </BootstrapTooltip>
        </h2>

        <div className='settings-actions compensation-actions'>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <TimeField
              label="Tiempo"
              format="mm:ss"
              value={compensationTimeToDayjs(localCompensationTime.time)}
              onChange={(newValue) => {
                setLocalCompensationTime(prev => ({
                  ...prev,
                  time: dayjsToCompensationTime(newValue)
                }));
              }}
              slotProps={{
                textField: {
                  variant: 'outlined',
                  placeholder: 'mm:ss',
                  sx: {
                    maxWidth: '110px',
                    '& .MuiInputLabel-root': {
                      color: 'var(--color-dark)',
                    },
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'var(--color-dark-variant)',
                      },
                      '&:not(.Mui-focused):hover fieldset': {
                        borderColor: 'var(--color-dark)',
                      },
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '0.8rem',
                      padding: '0.5rem 12px',
                      color: 'var(--color-dark)',
                    }
                  }
                }
              }}
            />
          </LocalizationProvider>
          <Button
            variant="outlined"
            size="small"
            color={localCompensationTime.type === 'ahead' ? 'primary' : 'error'}
            onClick={() => setLocalCompensationTime(prev => ({
              ...prev,
              type: prev.type === 'ahead' ? 'late' : 'ahead'
            }))}
          >
            {localCompensationTime.type === 'ahead' ? 'Adelantados' : 'Retrasados'}
          </Button>
          <FormControlLabel
            control={
              <Switch
                checked={localCompensationMode}
                onChange={(e) => setLocalCompensationMode(e.target.checked)}
              />
            }
            labelPlacement='start'
            label={localCompensationMode ? 'Activado' : 'Desactivado'}
          />
          <Button
            variant="contained"
            color='success'
            size='small'
            onClick={handleSaveCompensation}
            disabled={!canSaveCompensation}
          >
            Guardar
          </Button>
        </div>
      </div>
      <div>
        <h2>Grupos personalizados</h2>
        <div className='settings-actions'>
          <button className="new_button" onClick={() => {
            resetGroupForm();
            setEditingGroup(null);
            setAvailableSchedules(structuredClone(userSchedules));
            setSelectedSchedules([]);
            setShowedLayout(LAYOUTS.NEW);
          }}>
            Nuevo +
          </button>
          <FormControlLabel
            control={
              <Switch
                checked={localPeriodic}
                onChange={handlePeriodicChange}
                disabled={isUpdatingPeriodic}
              />
            }
            labelPlacement='start'
            label="Cambio periódico"
          />
          <BootstrapTooltip title="Si está habilitado, los grupos habilitados se aplicarán automáticamente a la hora establecida en cada día que el cambio periódico realice su acción." placement="right">
            <span className="material-symbols-rounded" style={{ fontSize: '18px', color: 'var(--color-dark-variant)', cursor: 'pointer', alignSelf: 'center' }}>
              help
            </span>
          </BootstrapTooltip>
        </div>
        <div className='settings-content'>

          {
            schedulesGroups && schedulesGroups.length > 0 ? (
              schedulesGroups.map(group => {
                const affectedCount = Array.isArray(group.schedules)
                  ? group.schedules.flat().length
                  : 0;

                return (
                  <div key={group._id} className='group-card'>
                    <div className='group-info'>

                      <h3>{group.name}</h3>
                      <p>
                        {affectedCount} horario(s) afectado(s)
                      </p>
                      <p>
                        Hora: {group.startTime ? group.startTime : "--:--"}
                      </p>
                      {
                        localPeriodic && (
                          <p>
                            {group.enabled ? "Habilitado" : "Deshabilitado"}
                          </p>
                        )
                      }
                    </div>

                    <div className='group-actions'>
                      <button
                        className="view_button"
                        onClick={() => handleViewGroup(group)}
                      >
                        Ver
                      </button>
                      <button
                        className="edit_button"
                        onClick={() => openEditGroup(group)}
                      >
                        Editar
                      </button>
                      <button
                        className="delete_button"
                        onClick={() => handleDeleteGroup(group)}
                      >
                        Eliminar
                      </button>
                      <button
                        className="apply_button"
                        onClick={() => handleApplyGroup(group)}
                      >
                        Aplicar grupo
                      </button>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className='empty-groups'>
                No hay grupos personalizados creados.
              </div>
            )
          }

        </div>
      </div>
      {/* Modal de confirmación de eliminación */
        isDeleteModalOpen && (
          <ConfirmModal
            title={'Eliminar grupo'}
            text={`¿Estás seguro de que deseas eliminar el grupo "${groupToDelete ? groupToDelete.name : ''}"? Esta acción no se puede deshacer.`}
            isOpen={isDeleteModalOpen}
            onClose={() => setIsDeleteModalOpen(false)}
            confirmFunction={confirmDeleteGroup}
          />
        )}

      {
        isViewGroupOpen && viewGroup && (
          <div className="group-view-modal" onClick={() => setIsViewGroupOpen(false)}>
            <div className="group-view-box" onClick={(e) => e.stopPropagation()}>
              <h2>{viewGroup.name}</h2>
              <p>
                Hora: {viewGroup.startTime || "--:--"}
              </p>
              <p>
                Hora: {viewGroup.enabled ? "Habilitado" : "Deshabilitado"}
              </p>

              <div className="group-view-list">
                {
                  viewGroup.schedulesParsed.map(schedule => {
                    const vehicle = devices.find(d => d.id === schedule.vehicleId);

                    return (
                      <div
                        key={schedule._id}
                        className="group-view-item"
                      >
                        <span>
                          {schedule.name}
                        </span>
                        <span>
                          {vehicle ? vehicle.label : "Sin vehículo"}
                        </span>
                        <Button
                          size="small"
                          onClick={() => {
                            setPrevSchedule([schedule]);
                            setOpenViewModal(true);
                          }}
                        >
                          Ver
                        </Button>
                      </div>
                    );
                  })
                }
              </div>
              <Button
                variant="text"
                color='error'
                size="small"
                onClick={() => setIsViewGroupOpen(false)}
              >
                Cerrar
              </Button>
            </div>
          </div>
        )
      }
      {/* Modal de vista previa */
        openViewModal && (
          <ViewSchedulesModal open={openViewModal} onClose={() => setOpenViewModal(false)} schedules={prevSchedule} prev={true} scheduleRegister={(schedules) => { setShowedLayout(0); scheduleRegister(schedules) }} onlyView={true} />
        )}
      {
        isApplyModalOpen && groupToApply && (
          <ConfirmModal
            title="Aplicar grupo"
            text={`Se actualizarán ${groupToApply.schedules?.flat().length || 0
              } horario(s). ¿Desea continuar?`}
            isOpen={isApplyModalOpen}
            onClose={() => setIsApplyModalOpen(false)}
            confirmFunction={confirmApplyGroup}
          />
        )
      }
    </>
  );

  const renderNewGroup = () => (
    <div className='group-form'>
      <Button onClick={() => { resetGroupForm(); setShowedLayout(0); }} style={{ alignSelf: 'flex-start', fontSize: '0.7rem', alignItems: 'flex-start' }}>
        <span className="material-symbols-rounded icon" style={{ fontSize: '18px' }}>arrow_back</span>
        Volver
      </Button>
      <h2>Nuevo Grupo</h2>

      {renderGroupForm(false)}

      {renderListsAndActions()}

      <ConfirmModal
        title={'Confirmación'}
        text={'Este vehículo ya se encuentra asignado a otro horario dentro del grupo. ¿Desea reasignarlo de cualquier manera?'}
        isOpen={isConfirmModalOpen}
        onClose={setIsConfirmModalOpen}
        confirmFunction={() => {
          if (pendingVehicleChange) {
            applyVehicleChange(
              pendingVehicleChange.scheduleId,
              pendingVehicleChange.parsedVehicleId
            );
          }
          setIsConfirmModalOpen(false);
        }}
      />
      {/* Modal de vista previa */
        openViewModal && (
          <ViewSchedulesModal open={openViewModal} onClose={() => setOpenViewModal(false)} schedules={prevSchedule} prev={true} scheduleRegister={(schedules) => { setShowedLayout(0); scheduleRegister(schedules) }} onlyView={true} />
        )}
    </div>
  );

  const renderEditGroup = () => (
    <div className='group-form'>

      <Button
        onClick={() => {
          resetGroupForm();
          setEditingGroup(null);
          setShowedLayout(LAYOUTS.SETTINGS);
        }}
        style={{
          alignSelf: 'flex-start',
          fontSize: '0.7rem',
          alignItems: 'flex-start'
        }}
      >
        <span className="material-symbols-rounded icon" style={{ fontSize: '18px' }}>arrow_back</span>
        Volver
      </Button>

      <h2>Editar Grupo</h2>

      {renderGroupForm(true)}

      {renderListsAndActions()}

    </div>
  );

  const renderGroupForm = (isEdit = false) => (
    <>
      <div className='form-container'>
        <TextField
          label="Nombre del grupo"
          value={groupName}
          onChange={(e) => setGroupName(e.target.value)}
          error={nameExists}
          helperText={nameExists ? "Ya existe un grupo con este nombre" : ""}
          fullWidth
          sx={{
            flex: '0 1 200px',
            '& .MuiInputLabel-root': {
              color: 'var(--color-dark)',
              fontSize: '0.8rem',
            },
            '& .MuiInputLabel-shrink': {
              fontSize: '1rem',
            },
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: 'var(--color-dark-variant)',
              },
              '&:not(.Mui-focused):hover fieldset': {
                borderColor: 'var(--color-dark)',
              },
            },
            '& .MuiInputBase-input': {
              color: 'var(--color-dark)',
              fontSize: '0.8rem',
              padding: '1rem 12px'
            },
          }}
        />

        <TextField
          label="Hora de aplicación"
          type="time"
          value={groupTime}
          onChange={(e) => setGroupTime(e.target.value)}
          slotProps={{
            inputLabel: {
              shrink: true,
            },
          }}
          sx={{
            flex: '0 1 auto',
            '& .MuiInputLabel-root': {
              color: 'var(--color-dark)', // Cambia el color de la label
            },
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: 'var(--color-dark-variant)', // Color del borde por defecto
              },
              '&:not(.Mui-focused):hover fieldset': {
                borderColor: 'var(--color-dark)', // Color del borde al pasar el mouse
              },
            },
            '& .MuiInputBase-input': {
              color: 'var(--color-dark)',
              fontSize: '0.8rem', // Tamaño del texto de entrada
              padding: '1rem 12px'
            },
          }}
        />

        <FormControlLabel
          control={
            <Switch
              checked={groupEnabled}
              onChange={(e) => setGroupEnabled(e.target.checked)}
            />
          }
          label="Habilitado para cambio periódico"
          sx={{ flex: '0 1 auto' }}
        />
        <BootstrapTooltip title="Si está habilitado, el grupo se aplicará automáticamente a la hora establecida en cada día que el cambio periódico realice su acción." placement="right">
          <span className="material-symbols-rounded" style={{ fontSize: '18px', color: 'var(--color-dark-variant)', cursor: 'pointer', alignSelf: 'center' }}>
            help
          </span>
        </BootstrapTooltip>
      </div>
    </>
  );

  const renderListsAndActions = () => (
    <>
      <div className="dual-lists">

        {/* Lista izquierda */}
        <div className="list-box">
          <h4>Horarios del grupo</h4>
          <div>
            {
              selectedSchedules.length === 0 ? (
                <p style={{ fontSize: '0.8rem', textAlign: 'center', fontStyle: 'italic', color: 'var(--color-dark-variant)' }}>
                  No hay horarios seleccionados. Haz click en un horario disponible para agregarlo al grupo.
                </p>
              )
                :
                selectedSchedules.map(schedule => (
                  <div key={schedule._id} className="list-item selected">

                    <span style={{ fontSize: '0.8rem', flex: 1 }}>{schedule.name}</span>

                    <select
                      id='schedules-group-select'
                      value={schedule.vehicleId || ''}
                      onChange={(e) =>
                        handleVehicleChange(schedule._id, e.target.value)
                      }
                      style={{
                        padding: '0.3rem',
                        borderRadius: '5px',
                        backgroundColor: 'var(--color-white)',
                        color: 'var(--color-dark)',
                        border: '1px solid var(--color-light)',
                        cursor: 'pointer',
                      }}
                    >
                      <option value="">No asignado</option>
                      {devices.map(device => (
                        <option key={device.id} value={device.id}>
                          {device.label}
                        </option>
                      ))}
                    </select>

                    <BootstrapTooltip title="Ver" placement="bottom">
                      <Button variant='text' color='primary' size='small' onClick={(e) => {
                        e.stopPropagation();
                        setPrevSchedule([schedule]);
                        setOpenViewModal(true);
                      }}
                        sx={{ minWidth: '40px', height: '100%' }}>
                        <span className="material-symbols-rounded" style={{ fontSize: '18px' }}>visibility</span>
                      </Button>
                    </BootstrapTooltip>
                    <BootstrapTooltip title="Quitar" placement="bottom">
                      <Button variant='text' color='error' size='small' onClick={() => {
                        setAvailableSchedules([...availableSchedules, schedule]);
                        setSelectedSchedules(
                          selectedSchedules.filter(s => s._id !== schedule._id)
                        );
                      }}
                        sx={{ minWidth: '40px', height: '100%' }}
                      >
                        ❌
                      </Button>
                    </BootstrapTooltip>
                  </div>
                ))}
          </div>
        </div>

        {/* Lista derecha */}
        <div className="list-box">
          <h4>Horarios disponibles</h4>
          <div>
            {availableSchedules.map(schedule => (
              <div
                key={schedule._id}
                className="list-item"
                onClick={() => {
                  setSelectedSchedules([
                    ...selectedSchedules,
                    { ...schedule, vehicleId: null }
                  ]);
                  setAvailableSchedules(
                    availableSchedules.filter(s => s._id !== schedule._id)
                  );
                }}
              >
                <span style={{ fontSize: '0.8rem' }}>{schedule.name}</span>
                <BootstrapTooltip title="Ver" placement="bottom">
                  <Button variant='text' color='primary' size='small' onClick={(e) => {
                    e.stopPropagation();
                    setPrevSchedule([schedule]);
                    setOpenViewModal(true);
                  }}
                    sx={{ minWidth: '40px', height: '100%' }}
                  >
                    <span className="material-symbols-rounded" style={{ fontSize: '18px' }}>visibility</span>
                  </Button>
                </BootstrapTooltip>
              </div>
            ))}
          </div>
        </div>

      </div>
      <div className="form-actions">

        <Button
          onClick={() => {
            resetGroupForm();
            setEditingGroup(null);
            setShowedLayout(LAYOUTS.SETTINGS);
          }}
          variant="contained"
          color="error"
          size="small"
        >
          Cancelar
        </Button>

        <Button
          variant="contained"
          color="primary"
          size="small"
          disabled={!canSaveGroup}
          onClick={saveGroup}
        >
          Guardar grupo
        </Button>

      </div>
    </>
  );

  return (
    <section className='settings-container'>
      {renderLayout()}
    </section>
  )
}

export default Settings
